import { useCallback, useEffect, useState } from "react";
import { fetchCompanyInvoices } from "../api/companyApi";
import type { CompanyBillingInvoice } from "../api/types";
import { enterpriseInvoices } from "../data";
import { getAuthToken } from "../auth";

import { resolveInvoiceNatureKey } from "../components/InvoiceNatureBadge";

export type EnterpriseInvoiceRow = {
  insurer?: string;
  deadline?: import("../api/types").InvoiceDeadline | null;
  id: string;
  displayId: number;
  reference: string;
  hospital: string;
  company: string;
  fileName: string;
  /** Référence de la facture du prestataire (n° imprimé sur sa facture). */
  providerReference?: string;
  amount: number;
  submittedDate: string;
  period: string;
  displayStatus: "submitted" | "inReview" | "approved";
  invoiceNature?: "forfait_mensuel" | "hors_forfait" | null;
  isForfaitMensuel?: boolean;
};

function mapApiInvoice(row: CompanyBillingInvoice): EnterpriseInvoiceRow {
  const created = row.created_at ? new Date(row.created_at) : null;
  const submittedDate = created
    ? created.toLocaleDateString("fr-FR")
    : row.period_label;
  const nature = resolveInvoiceNatureKey({
    invoiceNature: row.invoice_nature,
    isForfaitMensuel: row.is_forfait_mensuel,
  });

  return {
    id: `billing-${row.id}`,
    displayId: row.id,
    reference: row.reference,
    hospital: row.hospital,
    company: row.company || row.label,
    fileName: row.reference,
    providerReference: row.label,
    amount: row.amount_usd,
    submittedDate,
    period: row.period_label,
    displayStatus: row.display_status,
    invoiceNature: nature,
    isForfaitMensuel: nature === "forfait_mensuel",
    insurer: row.insurer ?? "",
    deadline: row.deadline ?? null,
  };
}

function mapDemoInvoice(
  invoice: (typeof enterpriseInvoices)[number],
): EnterpriseInvoiceRow {
  const displayStatus =
    invoice.status === "approved"
      ? "approved"
      : invoice.reviewStatus === "submitted"
        ? "submitted"
        : "inReview";

  return {
    id: invoice.id,
    displayId: invoice.displayId,
    reference: invoice.reference,
    hospital: invoice.hospital,
    company: invoice.company,
    fileName: invoice.fileName,
    amount: invoice.amount,
    submittedDate: invoice.submittedDate,
    period: invoice.period,
    displayStatus,
  };
}

export function useEnterpriseInvoices(query: { status?: "published" | "paid" | "approved" } = {}) {
  const [invoices, setInvoices] = useState<EnterpriseInvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setInvoices(enterpriseInvoices.map(mapDemoInvoice));
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const rows = await fetchCompanyInvoices(
        query.status ? { status: query.status } : {},
      );
      setInvoices(rows.map(mapApiInvoice));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les factures.";
      setError(message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [query.status]);

  useEffect(() => {
    void load();
  }, [load]);

  return { invoices, loading, error, usingApi, reload: load };
}
